import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { X, Lock } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Discreet server-validated preview access modal.
 * Never stores the access code in localStorage or the URL.
 */
export default function PreviewAccessModal({ open, onClose, onSuccess }) {
  const { i18n } = useTranslation();
  const isNl = i18n.language === "nl";
  const [code, setCode] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setCode("");
      setError(null);
      setLoading(false);
      return;
    }
    setTimeout(() => inputRef.current?.focus(), 60);
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim() || loading) return;
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API}/api/preview/login`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ access_code: code }),
      });
      if (r.ok) {
        setCode("");
        onSuccess?.();
        return;
      }
      if (r.status === 429) {
        setError(isNl
          ? "Te veel pogingen. Probeer het over een paar minuten opnieuw."
          : "Too many attempts. Please try again in a few minutes.");
      } else {
        setError(isNl ? "Ongeldige toegangscode." : "Invalid access code.");
      }
    } catch (err) {
      setError(isNl ? "Verbinding mislukt. Probeer opnieuw." : "Connection failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="preview-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="fixed inset-0 z-[999] flex items-center justify-center bg-black/80 p-6 backdrop-blur-md"
          onClick={onClose}
          data-testid="preview-modal-backdrop"
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-3xl border border-white/[0.08] bg-bht-navy/90 p-8 shadow-2xl backdrop-blur-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="preview-modal-title"
            data-testid="preview-modal"
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute right-4 top-4 rounded-full p-2 text-white/50 hover:bg-white/5 hover:text-white"
              aria-label={isNl ? "Sluiten" : "Close"}
              data-testid="preview-modal-close"
            >
              <X size={16} />
            </button>
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-bht-gold/40 bg-bht-void">
              <Lock size={20} weight="thin" className="text-bht-gold" />
            </div>
            <h2 id="preview-modal-title" className="mt-6 font-serif text-2xl leading-tight tracking-tight">
              {isNl ? "Team-toegang" : "Team access"}
            </h2>
            <p className="mt-2 text-sm text-white/60">
              {isNl
                ? "Voer de toegangscode in om de volledige website in preview-modus te bekijken."
                : "Enter the access code to view the full website in preview mode."}
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="preview-modal-form">
              <label className="block">
                <span className="sr-only">{isNl ? "Toegangscode" : "Access code"}</span>
                <input
                  ref={inputRef}
                  type="password"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder={isNl ? "Toegangscode" : "Access code"}
                  autoComplete="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  disabled={loading}
                  className="w-full rounded-full border border-white/10 bg-white/[0.03] px-5 py-3.5 text-sm text-white placeholder:text-white/35 transition-colors focus:border-bht-gold/60 focus:outline-none disabled:opacity-50"
                  data-testid="preview-modal-input"
                />
              </label>
              {error && (
                <div className="rounded-xl border border-red-500/30 bg-red-500/[0.06] px-4 py-3 text-xs text-red-200" data-testid="preview-modal-error" role="alert">
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={loading || !code.trim()}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-bht-gold px-6 py-3.5 text-[13px] font-semibold uppercase tracking-[0.14em] text-bht-void transition-transform hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:scale-100"
                data-testid="preview-modal-submit"
              >
                {loading
                  ? (isNl ? "Controleren…" : "Checking…")
                  : (isNl ? "Toegang verlenen" : "Enter Preview")}
              </button>
              <p className="text-center text-[10px] uppercase tracking-widest text-white/35">
                {isNl ? "Alleen voor BHT-team en genodigden" : "For BHT team and invited guests only"}
              </p>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
